/**
 * Shared helpers for packaging form fields: constrained inputs, normalization, API mapping.
 * Add future logic here (e.g. paste handling, other numeric fields) as needed.
 */

import { decodeHtmlEntities } from "@/utils/helper";

/** Default max for whole-number percent fields (e.g. 0–100). */
const INTEGER_PERCENT_MAX_DEFAULT = 100;

/** Digit width for integers in [0, max] (e.g. max 100 → 3 for `"100"`). */
export function integerPercentMaxDigitCount(
  max: number = INTEGER_PERCENT_MAX_DEFAULT
): number {
  return Math.max(1, String(Math.trunc(Math.abs(max))).length);
}

/**
 * Take at most that many **leading** digits, then remove trailing digits until value ≤ `max`.
 * Same rules for typing and paste: `3000` → `"30"`, `10000` → `"100"`, `9999` → `"99"` (max 100).
 */
export function sanitizeIntegerPercentDigits(
  raw: string,
  max: number = INTEGER_PERCENT_MAX_DEFAULT
): string {
  const cap = integerPercentMaxDigitCount(max);
  const rawStr = String(raw ?? "").trim();
  if (rawStr === "") return "";
  const allDigits = rawStr.replace(/\D/g, "");
  let digits = allDigits.slice(0, cap);
  while (digits.length > 0) {
    const n = Number(digits);
    if (Number.isFinite(n) && n <= max) break;
    digits = digits.slice(0, -1);
  }
  return digits;
}

// --- Gram weight (9999.99 shape: 4 + 2 digits); collapse `..`, strip junk ---

const GRAM_WEIGHT_MAX_DEFAULT = 9999.99;

/** One decimal point, max 4 int + 2 frac digits. `preserveTrailingDot` for `12.|` while typing. */
function gramWeightString(raw: string, preserveTrailingDot: boolean): string {
  let s = raw.trim().replace(/[^\d.]/g, "");
  const i = s.indexOf(".");
  if (i >= 0) s = s.slice(0, i + 1) + s.slice(i + 1).replace(/\./g, "");
  if (s === "" || s === ".") return s;
  const d = s.indexOf(".");
  if (d < 0) return (s.replace(/^0+/, "") || "0").slice(0, 4);
  const int0 = s.slice(0, d);
  let frac = s.slice(d + 1);
  const trail = frac === "" && s.endsWith(".") && preserveTrailingDot;
  const int = int0 ? (int0.replace(/^0+/, "") || "0").slice(0, 4) : "";
  frac = frac.slice(0, 2);
  if (trail) return int ? `${int}.` : ".";
  if (!int0 && frac) return `.${frac}`;
  if (!frac) return int || "0";
  return `${int}.${frac}`;
}

export type IntegerPercentBlurResult =
  | { kind: "cleared" }
  | { kind: "clamped"; value: number }
  | { kind: "invalid" };

/**
 * Normalizes on blur: empty → cleared (no value); explicit numbers → clamped integer [0, max].
 */
export function normalizeIntegerPercentBlur(
  raw: string,
  max: number = INTEGER_PERCENT_MAX_DEFAULT
): IntegerPercentBlurResult {
  const trimmed = String(raw ?? "").trim();
  if (trimmed === "") return { kind: "cleared" };
  const n = Number(trimmed);
  if (!Number.isFinite(n)) return { kind: "invalid" };
  const clamped = Math.min(max, Math.max(0, Math.round(n)));
  return { kind: "clamped", value: clamped };
}

/** Maps form field value to API payload; empty → `null`, 0 and other values → clamped integer. */
export function toIntegerPercentApiValue(
  v: unknown,
  max: number = INTEGER_PERCENT_MAX_DEFAULT
): number | null {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(v);
  if (!Number.isFinite(n)) return null;
  return Math.min(max, Math.max(0, Math.round(n)));
}


function roundToDecimalPlaces(n: number, decimals: number): number {
  const f = 10 ** decimals;
  return Math.round(n * f) / f;
}

export function acceptGramWeightDecimalInput(
  inputValue: string,
  max: number = GRAM_WEIGHT_MAX_DEFAULT
): string | null {
  const next = gramWeightString(inputValue, true);
  if (next === "" || next === ".") return next;
  const n = parseFloat(next);
  if (!Number.isFinite(n)) return null;
  if (n > max) return String(roundToDecimalPlaces(max, 2));
  return next;
}

export type GramWeightBlurResult =
  | { kind: "cleared" }
  | { kind: "clamped"; value: number }
  | { kind: "invalid" };

/**
 * Normalizes on blur: empty → cleared; otherwise clamp to [0, max] and round to 2 decimals.
 */
export function normalizeGramWeightBlur(
  raw: string,
  max: number = GRAM_WEIGHT_MAX_DEFAULT
): GramWeightBlurResult {
  const trimmed = String(raw ?? "").trim();
  if (trimmed === "" || trimmed === ".") return { kind: "cleared" };
  const digitLimited = gramWeightString(trimmed, false);
  if (digitLimited === "") return { kind: "invalid" };
  const n = parseFloat(digitLimited);
  if (!Number.isFinite(n)) return { kind: "invalid" };
  const clamped = Math.min(
    max,
    Math.max(0, roundToDecimalPlaces(n, 2))
  );
  return { kind: "clamped", value: clamped };
}

/** Normalizes loaded / saved gram weight; empty → `null`, numbers → clamped (0–max, 2 decimals). */
export function toGramWeightFormValue(
  v: unknown,
  max: number = GRAM_WEIGHT_MAX_DEFAULT
): number | null {
  if (v === null || v === undefined || v === "") return null;
  const limited = gramWeightString(String(v), false);
  if (limited === "") return null;
  const n = Number(limited);
  if (!Number.isFinite(n)) return null;
  return Math.min(max, Math.max(0, roundToDecimalPlaces(n, 2)));
}

/** String for API payload (digit-normalized, clamped 0–max, 2 decimals). */
export function toGramWeightApiString(
  v: unknown,
  max: number = GRAM_WEIGHT_MAX_DEFAULT
): string {
  const n = toGramWeightFormValue(v, max);
  if (n === null) return "";
  return String(n);
}

/**
 * Replaces HTML `<br>` elements with newlines using index scans only (no tag-stripping regex).
 */
function replaceBrTagsWithNewlines(html: string): string {
  let rest = html.replace(/\r\n/g, "\n");
  let out = "";
  while (rest.length > 0) {
    const lower = rest.toLowerCase();
    const i = lower.indexOf("<br");
    if (i === -1) {
      out += rest;
      break;
    }
    out += rest.slice(0, i) + "\n";
    const gt = rest.indexOf(">", i);
    if (gt === -1) {
      break;
    }
    rest = rest.slice(gt + 1);
  }
  return out;
}

/**
 * Removes `<...>` tag sequences by scanning; unclosed `<` (e.g. `<script` with no `>`)
 * drops the remainder so injection fragments cannot survive. No regex-based tag stripping.
 */
function removeHtmlTagsByScanning(html: string): string {
  let out = "";
  let i = 0;
  while (i < html.length) {
    const lt = html.indexOf("<", i);
    if (lt === -1) {
      out += html.slice(i);
      break;
    }
    out += html.slice(i, lt);
    const gt = html.indexOf(">", lt);
    if (gt === -1) {
      break;
    }
    i = gt + 1;
  }
  return out;
}

/**
 * Turns cooking-instructions content (often `contentEditable` innerHTML) into plain text
 * for API payloads: no tags, HTML entities decoded. Avoids `isomorphic-dompurify` here so
 * this module stays safe for server bundles (JSDOM default-stylesheet prerender issues).
 */
export function cookingInstructionsHtmlToPlainText(raw: unknown): string {
  const s = raw == null ? "" : String(raw);
  if (s.trim() === "") return "";
  const withNewlines = replaceBrTagsWithNewlines(s);
  const noTags = removeHtmlTagsByScanning(withNewlines);
  const decoded = decodeHtmlEntities(noTags);
  return typeof decoded === "string" ? decoded : String(decoded ?? "");
}
